import type { RoomAdmission } from '../../api/generated/model';

declare const sfuEndpointBrand: unique symbol;

/**
 * Endereco de sinalizacao do SFU, **ja conferido**.
 *
 * O tipo e opaco de proposito. `LiveRoom` so aceita um `SfuEndpoint`, e o unico
 * jeito de produzir um e passar por `sfuEndpointOf`: quem tentar montar o
 * LiveKit com o `serverUrl` cru da admissao nao compila. E o mesmo desenho do
 * `ownedOccurrence` do backend — a conferencia vive no tipo, e nao na disciplina
 * de quem escreve a proxima tela.
 *
 * A ALTERNATIVA RECUSADA foi um `if (!url.startsWith('ws'))` dentro do
 * componente. Um `if` protege a chamada que o carrega hoje e nenhuma das
 * proximas; e a razao de existir esta conferencia e justamente que o servidor
 * ja devolve algo que nao e endereco (ver `sfuEndpointOf`).
 */
export type SfuEndpoint = string & { readonly [sfuEndpointBrand]: 'SfuEndpoint' };

/**
 * Por que o endereco devolvido pela admissao nao serve.
 *
 * `received` viaja junto porque a recusa precisa ter NOME na tela: dizer "nao
 * conectou" manda a pessoa tentar de novo para sempre; mostrar o valor que
 * chegou identifica de quem e a pendencia em um olhar.
 */
export interface SfuRefusal {
  readonly cause: 'missing' | 'not_an_address';
  readonly received: string;
}

export type SfuEndpointResult =
  | { readonly ok: true; readonly endpoint: SfuEndpoint }
  | { readonly ok: false; readonly refusal: SfuRefusal };

/**
 * Esquemas que o cliente do LiveKit sabe discar. `http`/`https` entram porque a
 * biblioteca normaliza para `ws`/`wss`; um `mailto:` ou um identificador solto
 * nao entram.
 *
 * O que a conferencia NAO faz e exigir TLS: transporte em claro e decisao de
 * infraestrutura (o `ws://localhost:7880` do ambiente de desenvolvimento e
 * legitimo), e recusar aqui derrubaria uma configuracao que funciona.
 */
const DIALABLE_SCHEMES: ReadonlySet<string> = new Set(['ws:', 'wss:', 'http:', 'https:']);

/**
 * Confere o `serverUrl` da admissao antes de qualquer tela tentar conectar.
 *
 * ISTO NAO E PARANOIA: em 15/08/2026 o backend devolve o **identificador da
 * sala** neste campo. `api/internal/room/service.go:422` termina com
 * `return token, handle.ID, ...`, e `handle.ID` e `"room-" + occurrenceID`
 * (`api/internal/media/mediafake/fake.go:107`) — enquanto o contrato declara
 * `serverUrl` como `format: uri` (`contract/openapi.yaml:6113`). O front e
 * escrito contra o contrato; esta funcao e o ponto onde a diferenca entre o
 * contrato e o servidor de hoje vira uma frase legivel em vez de uma excecao
 * vinda de dentro do `livekit-client`.
 */
export function sfuEndpointOf(admission: RoomAdmission): SfuEndpointResult {
  const received = admission.serverUrl;
  const raw = received.trim();
  if (raw === '') return { ok: false, refusal: { cause: 'missing', received } };

  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    // `new URL('room-3f2a...')` lanca: nao ha esquema, entao nao ha endereco.
    return { ok: false, refusal: { cause: 'not_an_address', received } };
  }

  if (!DIALABLE_SCHEMES.has(parsed.protocol)) {
    return { ok: false, refusal: { cause: 'not_an_address', received } };
  }

  return { ok: true, endpoint: raw as SfuEndpoint };
}
